import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';
import { get } from 'lodash-es';
import {
  BaseBox,
  Body1,
  FlexBox,
  SecondaryButton,
  DoconCollapse,
  PrimaryButton,
  TertiaryButton,
  Icons,
} from '@docon/docon-ui-library';
import RequestCancellationModal from '../Modal/RequestCancellation';
import CancelSuccessfulModal from '../Modal/CancelSuccessful';
import BookAppointmentModal from 'components/BookAppointment';
import { appointmentService } from 'services';
import { cleverTapAnl } from 'utilities';
import { getTimeFromMinutes } from 'utils';
import { colors } from 'utilities/colorConfig';
import { EventAppointmentReschedule } from 'utilities/clevertapEvent';

interface IProps {
  [key: string]: any;
  doctorID: string,
  doctorName: string,
  appointment: any;
  paidAmount: number,
  sessionId?: string,
  history: any;
  onUpdateAppointment?: () => void;
}

const Root = styled(BaseBox)`
  background: ${colors.white};

  ${DoconCollapse} {
    button {
      margin: 0;
      width: 100%;
      flex: 1;
      max-width: 50%;
      font-weight: 600;
      border-radius: 4px;
    }

    ${TertiaryButton} {
      padding: 2px 0;
      margin: 4px 0;
      height: auto;
      width: auto;
      max-width: 100%;
    }
  }
`;

const suggestionLists = [
  {
    title: 'Doctor did not join the call',
    key: 'notJoin',
  },
  {
    title: 'Prescription related issue',
    key: 'prescription',
  },
  {
    title: 'Refund not received',
    key: 'refund',
  },
  {
    title: 'Reschedule call',
    key: 'reschedule',
  },
  {
    title: 'Cancel call',
    key: 'cancel',
  },
];

const contentMap = {
  notJoin: 'We are sorry for the experience. Select your option below.',
  prescription: 'We would like to hear more from you about this issue. Write to us and we will get back to you as soon as possible.',
  refund: 'We are sorry for the experience. You can write to us about this issue and we will get back to you as soon as possible.',
  reschedule: 'You can reschedule the call and it will be updated with your Doctor or you can write to us more about it and we will get back to you as soon as possible.',
  cancel: 'Cancellation is allowed only till 30 minutes before the appointment time.',
};

function CollapseItem({
  name: key,
  isLoading,
  isRescheduleEligible,
  title,
  appointmentStatus,
  onButtonClick,
}: any) {

  let children: any = null;

  function handleBtnClick(event: any) {
    onButtonClick({
      btnID: event?.currentTarget?.id,
      title,
    });
  }

  switch (key) {
    case 'notJoin':
      children = (
        <>
          <TertiaryButton
            icon={isLoading ? <Icons.LoadingIcon width={16} /> : ''}
            hidden={appointmentStatus === 'cancelled'}
            id='requestCancellation'
            title='Request cancellation'
            onClick={handleBtnClick}
          />
          <TertiaryButton
            hidden={!isRescheduleEligible || appointmentStatus === 'cancelled'}
            id='reschedule'
            title='Reschedule'
            onClick={handleBtnClick}
          />
          <TertiaryButton id='contactUs' title='Contact us' onClick={handleBtnClick} />
        </>
      );
      break;
    case 'prescription':
    case 'refund':
      children = (
        <PrimaryButton
          id='contactUs'
          size='small'
          title='Contact us'
          onClick={handleBtnClick}
        />
      );
      break;

    case 'reschedule':
      children = (
        <FlexBox gap={'12px'}>
          <PrimaryButton
            id='reschedule'
            size='small'
            title='Reschedule'
            disabled={!isRescheduleEligible || appointmentStatus === 'cancelled'}
            onClick={handleBtnClick}
          />
          <SecondaryButton
            id='contactUs'
            size='small'
            title='Contact us'
            onClick={handleBtnClick}
          />
        </FlexBox>
      );
      break;
    case 'cancel':
      children = (
        <FlexBox gap={'12px'}>
          <PrimaryButton
            id='cancelCall'
            size='small'
            title='Cancel call'
            disabled={!isRescheduleEligible || appointmentStatus === 'cancelled'}
            onClick={handleBtnClick}
          />
          <SecondaryButton
            id='contactUs'
            size='small'
            title='Contact us'
            onClick={handleBtnClick}
          />
        </FlexBox>
      );
      break;

    default:
      break;
  }

  return (
    <>
      <Body1
        p='8px 0 12px'
        id={key}
        title={contentMap[key]}
      />
      {children}
    </>
  );
}

function HelpSuggestionLists({
  doctorID,
  doctorName,
  appointment,
  sessionId,
  paidAmount,
  history,
  onUpdateAppointment,
  ...props
}: IProps,
) {

  const {
    appointmentID,
    status: appointmentStatus,
    appointmentDate,
    appointmentDisplayTime,
    appointmentTime,
    isTeleConsult,
  } = appointment || {};

  const { user } = useSelector(state => state.auth);
  const { bookedAppointment } = useSelector(state => state.appointment);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalType, setModalType] = useState<any>(null);
  const [cancelTicketID, setCancelTicketID] = useState<any>(null);
  const [showCancelSuccessfulModal, setShowCancelSuccessfulModal] = useState<any>(false);
  const [openBookingModal, setOpenBookingModal] = useState<any>(false);
  const cancelReason = useRef('');
  const suggestionReason = useRef('');

  const duration = moment.duration(moment(appointmentDisplayTime).diff(moment()));
  const isRescheduleEligible = duration.asMinutes() > 30 && appointmentStatus !== 'cancelled';

  if (!isRescheduleEligible) {
    contentMap.reschedule = 'Rescheduling is allowed only till 30 minutes before the appointment time.';
  }

  function sendCleverTapEvent({ eventName, ...rest }: any) {
    cleverTapAnl.event({
      event_name: eventName,
      event: {
        page_name: 'appointment_help',
        appointment_status: appointmentStatus,
        appointment_id: appointmentID,
        teleconsult_id: sessionId,
        doctor_id: doctorID,
        appointment_date: moment(appointmentDate).format('YYYY-MM-DD'),
        slot_type: isTeleConsult ? 'video' : 'physical',
        slot_time: getTimeFromMinutes(appointmentTime),
        transaction_amount: paidAmount,
        ...rest,
      },
    });
  }

  function checkCancelEligibility(ctaId: string) {
    setIsSubmitting(true);
    appointmentService.checkCancelEligibility({ sessionId })
      .then((data: any = {}) => {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty('ticketID')) {
          const {
            status,
            ticketID,
          } = data || {};

          setCancelTicketID(ticketID);
          setModalType(status ? 'eligible' : 'notEligible');
        } else {
          const duration = moment.duration(moment(appointmentDisplayTime).diff(moment()));
          const eventData: any = {
            eventName: 'appointment_cancel',
            cancel_time_to_appointment: Math.floor(duration.asMinutes()),
          };
          if (ctaId === 'requestCancellation') {
            eventData.eventName = 'appointment_request_cancellation';
            eventData.appointment_help_sub_reason = cancelReason.current;
          }
          sendCleverTapEvent(eventData);

          if (onUpdateAppointment)
            onUpdateAppointment();
        }
      })
      .catch((err) => {
        const statusCode = get(err, 'response.status');
        // const errorMsg = get(err, 'response.data.error');

        if (statusCode === 400)
          setModalType('notBefore30');
      })
      .finally(() => setIsSubmitting(false));
  }

  function cancelAppointment() {
    setIsSubmitting(true);
    appointmentService.cancelAppointment({ sessionId, ticketID: cancelTicketID })
      .then(() => {
        sendCleverTapEvent({
          eventName: 'appointment_request_cancellation',
          appointment_help_sub_reason: cancelReason.current,
          cancel_time_to_appointment: Math.floor(duration.asMinutes()),
        });
        setModalType(null);
        if (onUpdateAppointment)
          onUpdateAppointment();

        setShowCancelSuccessfulModal(true);
      })
      .catch(() => {
        // setModalType('notBefore30');
      })
      .finally(() => setIsSubmitting(false));
  }

  function handleContactUsClick({ title }: any = {}) {
    sendCleverTapEvent({
      eventName: 'help_contact_us',
      appointment_help_sub_reason: title || '-',
      help_time_to_appointment: Math.floor(duration.asMinutes()),
    });

    history.push({
      pathname: '/support/contactus',
      state: {
        isTeleConsult: isTeleConsult,
        doctorID: doctorID,
        doctorName: doctorName,
        appointmentID: appointmentID,
        appointmentStatus: appointmentStatus,
        appointmentDate: appointmentDate,
        appointmentTime: appointmentTime,
        appointmentDisplayTime: appointmentDisplayTime,
        sessionId: sessionId,
        paidAmount: paidAmount,
        reasonType: title || 'Request cancellation',
        email: user.email,
      },
    });
  }

  function handleConfirmClick() {
    cancelAppointment();
  }

  const handleModalClose = (modal: string) => ({ fetchAppointment, isReschedule }: any = {}) => {
    switch (modal) {
      case 'cancel':
        return setShowCancelSuccessfulModal(false);
      case 'appointmentBooking':
        if (fetchAppointment)
          onUpdateAppointment && onUpdateAppointment();
        if (isReschedule)
          triggerRescheduleEvent();
        return setOpenBookingModal(false);
      case 'requestCancel':
        return setModalType(null);
      default:
        break;
    }
  };

  function handleHeaderClick({ title }: any) {
    sendCleverTapEvent({
      eventName: 'appointment_help_sub_reason_select',
      appointment_help_sub_reason: title,
    });
  }

  function handleBtnClick({
    btnID,
    title,
  }: any) {
    suggestionReason.current = title;
    switch (btnID) {
      case 'contactUs':
        return handleContactUsClick({ title });
      case 'reschedule':
        return setOpenBookingModal(true);
      case 'cancelCall':
      case 'requestCancellation': {
        cancelReason.current = title;
        return checkCancelEligibility(btnID);
      }
      default:
        suggestionReason.current = '';
        break;
    }
  }

  function triggerRescheduleEvent() {
    const a = moment(appointment.appointmentDisplayTime || appointmentDate, 'YYYY-MM-DD HH:mm:ss ZZ');
    const timeDifferenceToAppointment = a.diff(moment(), 'minutes');
    const appointmentTime = bookedAppointment?.appointment?.appointmentTime || bookedAppointment?.appointmentTime;
    cleverTapAnl.event({
      event_name: 'appointment_reschedule',
      event: {
        page_name: 'appointment_help',
        doctor_id: doctorID,
        slot_time: getTimeFromMinutes(appointmentTime),
        slot_type: isTeleConsult ? 'video' : 'physical',
        teleconsult_id: appointment.teleConsultSessionID,
        appointment_id: appointmentID,
        appointment_status: appointmentStatus,
        appointment_date: moment(appointmentDate).format('YYYY-MM-DD'),
        patient_id: appointment.patientID,
        banner_id: '-',
        banner_label: '-',
        is_banner_active: '-',
        cta_label: 'Reschedule',
        appointment_help_sub_reason: suggestionReason.current,
        reschedule_time_to_appointment: timeDifferenceToAppointment,
        transaction_amount: appointment.teleConsultSessionObject?.pricing_details?.total || 0,
      },
    } as EventAppointmentReschedule);
  }

  return (
    <Root {...props}>
      {
        suggestionLists.map(list => (
          <DoconCollapse
            key={list.key}
            p='11px 27px'
            title={list.title}
            onHeaderClick={handleHeaderClick}
          >
            <CollapseItem
              name={list.key}
              isLoading={isSubmitting}
              isRescheduleEligible={isRescheduleEligible}
              key={list.key}
              title={list.title}
              appointmentStatus={appointmentStatus}
              onButtonClick={handleBtnClick}
            >
            </CollapseItem>
          </DoconCollapse>
        ))
      }

      <RequestCancellationModal
        requestType={modalType}
        doctorName={doctorName}
        isLoading={isSubmitting}
        onContactUsClick={handleContactUsClick}
        onConfirmClick={handleConfirmClick}
        onClose={handleModalClose('requestCancel')}
      />

      {
        showCancelSuccessfulModal ? (
          <CancelSuccessfulModal
            doctorName={doctorName}
            appointmentDate={appointmentDate}
            appointmentTime={appointmentTime}
            onClose={handleModalClose('cancel')}
          />
        ) : null
      }

      {
        openBookingModal ? (
          <BookAppointmentModal
            isOpen
            isReschedule
            appointment={appointment}
            doctor={{ ...appointment.doctordetails, clinicID: appointment.clinicID }}
            onClose={handleModalClose('appointmentBooking')}
          />
        ) : null
      }
    </Root>
  );
}

export default HelpSuggestionLists;